// Agent/Skill package browser (roadmap item 149 Phase B).
//
// Mounted at `/repos/:id/agentskill` for any repository whose `format` is
// `agentskill`. Phase A (`api/internal/formats/agentskill`) already parses
// every published tarball's manifest into the artifact's JSONB `metadata`
// column, so this page needs no new backend endpoint — it reuses the
// generic `useRepoArtifacts` hook (same one RepoDetail's Contents tab
// calls) and groups rows client-side by `metadata.name`, mirroring the
// Ollama/Kustomize/OPA browsers' catalog-then-detail shape.
//
// Deliberately out of scope for this batch (left for a follow-up, same
// pattern as item 140's staged batches): a live "signed / scanned /
// released" policy-status readout. No per-artifact endpoint exposing that
// as a single field exists yet in the frontend API surface; each version
// row instead links to the existing generic `/artifacts/:id` page, which
// already renders provenance/build-info when available.

import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Bot,
  Copy,
  Download,
  ExternalLink,
  FileText,
  MessageSquare,
  Package,
  Plug,
  Server,
  ShieldQuestion,
  Wrench,
} from 'lucide-react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { useProjects, useRepoDetail, useRepoArtifacts, type RepoArtifact } from '../../api/client';

const PACKAGE_KIND_DISCRIMINATOR = 'agentskill.package';

function bytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function shortHex(d?: string): string {
  if (!d) return '';
  if (d.length <= 19) return d;
  return d.slice(0, 12) + '…' + d.slice(-4);
}

const KIND_ICONS: Record<string, typeof Bot> = {
  skill: Wrench,
  agent: Bot,
  'mcp-server': Server,
  prompt: MessageSquare,
};

const KIND_LABEL_KEYS: Record<string, string> = {
  skill: 'skill',
  agent: 'agent',
  'mcp-server': 'mcpServer',
  prompt: 'prompt',
};

function kindMeta(
  t: ReturnType<typeof useTranslation>['t'],
  packageKind: unknown,
): { label: string; icon: typeof Bot } {
  const k = typeof packageKind === 'string' ? packageKind : '';
  const labelKey = KIND_LABEL_KEYS[k];
  return {
    label: labelKey
      ? t(`agentSkillPackages.kinds.${labelKey}`)
      : k || t('agentSkillPackages.kinds.unknown'),
    icon: KIND_ICONS[k] ?? ShieldQuestion,
  };
}

interface SkillGroup {
  name: string;
  packageKind: unknown;
  description: string;
  versions: RepoArtifact[];
}

function groupPackages(artifacts: RepoArtifact[] | undefined): SkillGroup[] {
  if (!artifacts) return [];
  const byName = new Map<string, RepoArtifact[]>();
  for (const a of artifacts) {
    if (a.metadata?.kind !== PACKAGE_KIND_DISCRIMINATOR) continue;
    const name = typeof a.metadata.name === 'string' ? a.metadata.name : a.path;
    const list = byName.get(name) ?? [];
    list.push(a);
    byName.set(name, list);
  }
  const groups: SkillGroup[] = [];
  for (const [name, versions] of byName) {
    versions.sort((x, y) => (x.uploaded_at < y.uploaded_at ? 1 : -1));
    const latest = versions[0];
    groups.push({
      name,
      packageKind: latest.metadata?.package_kind,
      description:
        typeof latest.metadata?.description === 'string' ? latest.metadata.description : '',
      versions,
    });
  }
  groups.sort((a, b) => (a.versions[0].uploaded_at < b.versions[0].uploaded_at ? 1 : -1));
  return groups;
}

export function AgentSkillPackages() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: repo, isLoading: repoLoading, error: repoError } = useRepoDetail(id);
  const { data: projects } = useProjects();
  const projectSlug = useMemo(
    () => projects?.find((p) => p.id === repo?.projectId)?.slug,
    [projects, repo?.projectId],
  );
  const { data: artifacts, error: artifactsError, isLoading: artifactsLoading } =
    useRepoArtifacts(repo?.id, 1000);
  const groups = useMemo(() => groupPackages(artifacts), [artifacts]);
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const selected = groups.find((g) => g.name === selectedName) ?? null;

  if (repoLoading) {
    return <div className="p-6 text-fg-muted">{t('agentSkillPackages.loadingRepo')}</div>;
  }
  if (repoError || !repo) {
    return (
      <div className="p-6 text-danger">
        {t('agentSkillPackages.loadRepoFailed', { error: String(repoError ?? 'unknown') })}
      </div>
    );
  }
  if (repo.format !== 'agentskill') {
    return (
      <div className="p-6">
        <Link
          to={`/repos/${repo.id}`}
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('agentSkillPackages.backToRepo')}
        </Link>
        <p className="text-fg-muted">
          <Trans
            i18nKey="agentSkillPackages.wrongFormat"
            components={[<code key={0} className="orb-code" />]}
          />
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/repos/${repo.id}`}
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('agentSkillPackages.backTo', { name: repo.name })}
        </Link>
        <p className="orb-eyebrow text-accent/80 mb-1">{t('agentSkillPackages.eyebrow')}</p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">
          {repo.name}
        </h1>
        <p className="text-fg-muted text-sm mt-2 max-w-3xl">
          <Trans
            i18nKey="agentSkillPackages.intro"
            components={[<code key={0} className="orb-code" />]}
          />
        </p>
      </div>

      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Package className="h-4 w-4 text-accent" />
              {t('agentSkillPackages.catalog.title')}
            </span>
          }
          description={
            groups.length > 0
              ? t('agentSkillPackages.catalog.count', { count: groups.length })
              : undefined
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-fg-muted border-b border-border">
                <th className="px-4 py-2 font-medium">{t('agentSkillPackages.catalog.table.name')}</th>
                <th className="px-4 py-2 font-medium">{t('agentSkillPackages.catalog.table.kind')}</th>
                <th className="px-4 py-2 font-medium">{t('agentSkillPackages.catalog.table.description')}</th>
                <th className="px-4 py-2 font-medium">{t('agentSkillPackages.catalog.table.versions')}</th>
                <th className="px-4 py-2 font-medium">{t('agentSkillPackages.catalog.table.latest')}</th>
                <th className="px-4 py-2 font-medium">{t('agentSkillPackages.catalog.table.uploaded')}</th>
              </tr>
            </thead>
            <tbody>
              {artifactsLoading && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={6}>
                    {t('agentSkillPackages.catalog.loading')}
                  </td>
                </tr>
              )}
              {artifactsError && (
                <tr>
                  <td className="px-4 py-3 text-danger" colSpan={6}>
                    {String(artifactsError)}
                  </td>
                </tr>
              )}
              {groups.length === 0 && !artifactsLoading && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={6}>
                    <Trans
                      i18nKey="agentSkillPackages.catalog.empty"
                      values={{ projectSlug, repoSlug: repo.slug }}
                      components={[
                        <code key={0} className="orb-code" />,
                        <code key={1} className="orb-code" />,
                        <code key={2} className="orb-code" />,
                      ]}
                    />
                  </td>
                </tr>
              )}
              {groups.map((g) => {
                const { label, icon: Icon } = kindMeta(t, g.packageKind);
                const latest = g.versions[0];
                const isSelected = g.name === selectedName;
                return (
                  <tr
                    key={g.name}
                    className={`border-b border-border/50 cursor-pointer ${
                      isSelected ? 'bg-bg-hover/60' : 'hover:bg-bg-hover/40'
                    }`}
                    onClick={() => setSelectedName(g.name)}
                  >
                    <td className="px-4 py-2 font-medium text-fg">
                      <span className="inline-flex items-center gap-2">
                        <Icon className="h-3.5 w-3.5 text-accent" />
                        {g.name}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <Badge tone="accent">{label}</Badge>
                    </td>
                    <td className="px-4 py-2 text-fg-muted max-w-xs truncate" title={g.description}>
                      {g.description || '—'}
                    </td>
                    <td className="px-4 py-2 tabular-nums text-fg-muted">{g.versions.length}</td>
                    <td className="px-4 py-2">
                      <Badge tone="neutral">{latest.version}</Badge>
                    </td>
                    <td className="px-4 py-2 text-fg-muted">
                      {new Date(latest.uploaded_at).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {selected && (
        <SkillDetailCard group={selected} projectSlug={projectSlug} repoSlug={repo.slug} />
      )}

      <McpConnectCard />
    </div>
  );
}

// ---------------------------------------------------------------------------
// MCP connect panel (roadmap item 149 Phase C, frontend half)
// ---------------------------------------------------------------------------
//
// Phase C shipped the server itself (api/cmd/mcp-server + internal/mcpserver)
// on 2026-07-31, but nothing in the product ever mentioned it: no Makefile
// target built it, no release shipped it, and this page — the one place a
// user looks at agent/skill packages — said nothing. A read-only MCP server
// whose existence is only discoverable by reading the Go tree is not a
// feature. This panel is the discovery surface.
//
// It deliberately renders a *placeholder* token rather than minting one.
// Handing out a live credential from a page the user merely browsed to
// would create a token nobody tracked and nobody revokes; the link to
// /tokens keeps issuance where the audit trail already is.
//
// The base URL is taken from the browser's own origin, which is correct for
// the standard deployment (nginx serves the SPA and proxies /api to the API
// pods, so the SPA's origin *is* the API origin). Split-origin installs are
// called out in the hint text rather than guessed at.

interface McpSnippetProps {
  readonly label: string;
  readonly hint: string;
  readonly snippet: string;
}

function McpSnippet({ label, hint, snippet }: McpSnippetProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard write fails in non-secure contexts; the snippet is fully
      // visible and selectable, so there is nothing useful to report.
    }
  };
  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-1">
        <h3 className="text-sm font-medium text-fg">{label}</h3>
        <Button variant="ghost" size="sm" type="button" onClick={onCopy} title={label}>
          <Copy className="h-4 w-4" />
          {copied ? t('tokens.created.copied') : t('tokens.created.copy')}
        </Button>
      </div>
      <p className="text-xs text-fg-muted mb-2">{hint}</p>
      <pre className="bg-bg-inset border border-border rounded-md p-3 text-xs font-mono text-fg overflow-x-auto whitespace-pre">
        {snippet}
      </pre>
    </div>
  );
}

const MCP_TOOLS = ['list_skills', 'get_skill', 'get_policy_status', 'pull_skill'] as const;

function McpConnectCard() {
  const { t } = useTranslation();
  const origin = typeof window === 'undefined' ? 'https://registry.example.com' : window.location.origin;

  const binarySnippet = JSON.stringify(
    {
      mcpServers: {
        orbitalreg: {
          command: '/usr/local/bin/orbitalreg-mcp-server',
          env: {
            ORBITALREG_MCP_API_BASE_URL: origin,
            ORBITALREG_MCP_TOKEN: 'orbr_…',
          },
        },
      },
    },
    null,
    2,
  );

  const containerSnippet = JSON.stringify(
    {
      mcpServers: {
        orbitalreg: {
          command: 'docker',
          // `-i` is mandatory and `-t` must be absent: the protocol is a raw
          // JSON-RPC stream on stdio, and a TTY's line discipline corrupts it.
          args: [
            'run',
            '--rm',
            '-i',
            '-e',
            'ORBITALREG_MCP_API_BASE_URL',
            '-e',
            'ORBITALREG_MCP_TOKEN',
            'ghcr.io/orbitalreg/orbitalreg-mcp-server:latest',
          ],
          env: {
            ORBITALREG_MCP_API_BASE_URL: origin,
            ORBITALREG_MCP_TOKEN: 'orbr_…',
          },
        },
      },
    },
    null,
    2,
  );

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Plug className="h-4 w-4 text-accent" />
            {t('agentSkillPackages.mcp.title')}
          </span>
        }
        description={t('agentSkillPackages.mcp.subtitle')}
      />
      <CardBody className="space-y-5">
        <p className="text-sm text-fg-muted max-w-3xl">
          <Trans
            i18nKey="agentSkillPackages.mcp.intro"
            components={[<code key={0} className="orb-code" />]}
          />
        </p>

        <div>
          <h3 className="text-sm font-medium text-fg mb-2">
            {t('agentSkillPackages.mcp.toolsTitle')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {MCP_TOOLS.map((name) => (
              <Badge key={name} tone="neutral">
                <code className="font-mono">{name}</code>
              </Badge>
            ))}
          </div>
          <p className="text-xs text-fg-muted mt-2 max-w-3xl">
            {t('agentSkillPackages.mcp.toolsHint')}
          </p>
        </div>

        <McpSnippet
          label={t('agentSkillPackages.mcp.binaryLabel')}
          hint={t('agentSkillPackages.mcp.binaryHint')}
          snippet={binarySnippet}
        />
        <McpSnippet
          label={t('agentSkillPackages.mcp.containerLabel')}
          hint={t('agentSkillPackages.mcp.containerHint')}
          snippet={containerSnippet}
        />

        <p className="text-xs text-fg-muted">
          <Trans
            i18nKey="agentSkillPackages.mcp.tokenHint"
            components={[
              <Link key={0} to="/tokens" className="text-accent hover:underline" />,
              <code key={1} className="orb-code" />,
            ]}
          />
        </p>
      </CardBody>
    </Card>
  );
}

interface SkillDetailCardProps {
  readonly group: SkillGroup;
  readonly projectSlug: string | undefined;
  readonly repoSlug: string;
}

function SkillDetailCard({ group, projectSlug, repoSlug }: SkillDetailCardProps) {
  const { t } = useTranslation();
  const latest = group.versions[0];
  const meta = latest.metadata ?? {};
  const { label, icon: Icon } = kindMeta(t, group.packageKind);
  const readme = typeof meta.readme === 'string' ? meta.readme : '';

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Icon className="h-4 w-4 text-accent" />
            {group.name}
            <Badge tone="accent">{label}</Badge>
            <Badge tone="neutral">{latest.version}</Badge>
          </span>
        }
        description={group.description || t('agentSkillPackages.detail.noDescription')}
      />
      <CardBody>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <ManifestField label={t('agentSkillPackages.detail.fields.tools')} value={meta.tools} />
            <ManifestField
              label={t('agentSkillPackages.detail.fields.permissions')}
              value={meta.permissions}
            />
            <ManifestField label={t('agentSkillPackages.detail.fields.egress')} value={meta.egress} />
          </div>

          {meta.dependencies !== undefined && (
            <ManifestField
              label={t('agentSkillPackages.detail.fields.dependencies')}
              value={meta.dependencies}
            />
          )}

          <div>
            <h3 className="text-sm font-semibold text-fg mb-2 inline-flex items-center gap-2">
              <FileText className="h-3.5 w-3.5 text-accent" />
              SKILL.md
            </h3>
            {readme ? (
              <pre className="bg-bg-inset border border-border rounded-md p-3 text-xs font-mono text-fg overflow-x-auto whitespace-pre-wrap max-h-[420px]">
                {readme}
              </pre>
            ) : (
              <p className="text-fg-muted text-sm">
                <Trans
                  i18nKey="agentSkillPackages.detail.noReadme"
                  components={[<code key={0} className="orb-code" />]}
                />
              </p>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-fg mb-2">
              {t('agentSkillPackages.detail.versionsTitle', { count: group.versions.length })}
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-fg-muted border-b border-border">
                    <th className="px-3 py-2 font-medium">{t('agentSkillPackages.detail.versionsTable.version')}</th>
                    <th className="px-3 py-2 font-medium">{t('agentSkillPackages.detail.versionsTable.size')}</th>
                    <th className="px-3 py-2 font-medium">{t('agentSkillPackages.detail.versionsTable.sha256')}</th>
                    <th className="px-3 py-2 font-medium">{t('agentSkillPackages.detail.versionsTable.uploaded')}</th>
                    <th className="px-3 py-2 font-medium">{t('agentSkillPackages.detail.versionsTable.links')}</th>
                  </tr>
                </thead>
                <tbody>
                  {group.versions.map((v) => (
                    <tr key={v.id} className="border-b border-border/50">
                      <td className="px-3 py-2">
                        <Badge tone="neutral">{v.version}</Badge>
                      </td>
                      <td className="px-3 py-2 tabular-nums text-fg-muted">
                        {bytes(v.size_bytes)}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-fg-muted">
                        {shortHex(v.sha256)}
                      </td>
                      <td className="px-3 py-2 text-fg-muted">
                        {new Date(v.uploaded_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-3">
                          <Link
                            to={`/artifacts/${v.id}`}
                            className="inline-flex items-center gap-1 text-accent hover:underline"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            {t('agentSkillPackages.detail.detail')}
                          </Link>
                          <a
                            href={`/agentskill/${projectSlug}/${repoSlug}/${encodeURIComponent(
                              group.name,
                            )}/${encodeURIComponent(v.version)}/download`}
                            className="inline-flex items-center gap-1 text-fg-muted hover:text-fg"
                          >
                            <Download className="h-3.5 w-3.5" />
                            {t('agentSkillPackages.detail.download')}
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

interface ManifestFieldProps {
  readonly label: string;
  readonly value: unknown;
}

// Tools/permissions/egress/dependencies round-trip through the manifest as
// arbitrary JSON (see agentskill.go's parsePackage) — a plain string array,
// an array of objects, or something else entirely. Render the common shapes
// legibly and fall back to pretty-printed JSON for everything else.
function ManifestField({ label, value }: ManifestFieldProps) {
  const { t } = useTranslation();
  return (
    <div>
      <h4 className="text-fg-muted text-xs uppercase tracking-wide mb-2">{label}</h4>
      {renderManifestValue(value, t)}
    </div>
  );
}

function renderManifestValue(value: unknown, t: ReturnType<typeof useTranslation>['t']) {
  if (value === undefined || value === null) {
    return <p className="text-fg-muted text-sm">{t('agentSkillPackages.detail.notDeclared')}</p>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <p className="text-fg-muted text-sm">{t('agentSkillPackages.detail.noneDeclared')}</p>;
    }
    if (value.every((v) => typeof v === 'string' || typeof v === 'number')) {
      return (
        <ul className="flex flex-wrap gap-1.5">
          {value.map((v, i) => (
            <li key={`${v}-${i}`}>
              <Badge tone="neutral">{String(v)}</Badge>
            </li>
          ))}
        </ul>
      );
    }
    return (
      <pre className="bg-bg-inset border border-border rounded-md p-2 text-xs font-mono text-fg overflow-x-auto whitespace-pre max-h-48">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  if (typeof value === 'object') {
    return (
      <pre className="bg-bg-inset border border-border rounded-md p-2 text-xs font-mono text-fg overflow-x-auto whitespace-pre max-h-48">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  if (typeof value === 'symbol' || typeof value === 'function' || typeof value === 'bigint') {
    // Unreachable in practice — value comes from parsed JSON manifest
    // metadata, which never contains these types.
    return null;
  }
  return <p className="text-fg text-sm">{String(value)}</p>;
}
